import React, { useEffect, useState, Component } from 'react';
import { AlertTriangle, CheckCircle2, ChevronRight, ShieldCheck, Zap, Brain, Flame, Clock, Dumbbell, Star } from 'lucide-react';
// --- Hooks ---
function useCountdown(initialSeconds: number) {
  const [seconds, setSeconds] = useState(initialSeconds);
  useEffect(() => {
    if (seconds <= 0) return;
    const interval = setInterval(() => {
      setSeconds(prev => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [seconds]);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
// --- Shared Components ---
const CTAButton = ({
  children,
  className = '',
  href
}: {
  children: React.ReactNode;
  className?: string;
  href?: string;
}) => <button onClick={() => href ? (window.fbq && window.fbq('track', 'AddToCart'), window.open(href, '_blank')) : document.getElementById('precio')?.scrollIntoView({
  behavior: 'smooth',
  block: 'center'
})} className={`
    w-full md:w-auto bg-gold hover:bg-gold-light text-charcoal-900 
    font-oswald font-bold text-xl md:text-2xl py-4 px-8 rounded-sm
    transition-all duration-300 transform hover:scale-105 hover:shadow-[0_0_20px_rgba(212,168,67,0.4)]
    flex items-center justify-center gap-3 uppercase tracking-wider
    ${className}
  `} data-id="element-0">
    {children}
    <ChevronRight className="w-6 h-6" strokeWidth={3} data-id="element-1" />
  </button>;
const SectionDivider = () => <div className="w-full flex justify-center py-12" data-id="element-2">
    <div className="w-24 h-1 bg-gold/30 rounded-full" data-id="element-3"></div>
  </div>;
const EbookImage = ({
  className = ''
}: {
  className?: string;
}) => <div className={`relative group ${className}`} data-id="element-4">
    <div className="absolute -inset-4 bg-gold/20 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700" data-id="element-5"></div>
    <img src="https://cdn.magicpatterns.com/uploads/oWyt5DLDNQwBCWmmkAc72V/Portada_Ebook.png" alt="Código Testosterona 40+ Ebook Cover" className="relative z-10 w-full max-w-md mx-auto rounded-lg shadow-2xl shadow-black/50 border border-charcoal-700" data-id="element-6" />
  </div>;
// --- Sections ---
const StickyBar = () => {
  const timeLeft = useCountdown(5 * 60 + 41); // 5:41
  return <div className="fixed top-0 left-0 w-full bg-[#8B0000] z-50 border-b border-red-500/30 shadow-lg" data-id="element-7">
      <div className="container mx-auto px-4 py-3 flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 text-center" data-id="element-8">
        <div className="flex items-center gap-2 text-white font-medium text-sm md:text-base" data-id="element-9">
          <AlertTriangle className="w-5 h-5 text-gold animate-pulse" data-id="element-10" />
          <span data-id="element-11">
            <strong className="font-oswald tracking-wide" data-id="element-12">ATENCIÓN:</strong> El
            precio de lanzamiento con un 90% de descuento expira cuando el
            contador llegue a cero.
          </span>
        </div>
        <div className="font-oswald text-2xl md:text-3xl font-bold text-gold tracking-wider bg-black/30 px-3 py-1 rounded" data-id="element-13">
          [{timeLeft}]
        </div>
      </div>
    </div>;
};
const Hero = () => <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-4 relative overflow-hidden" data-id="element-14">
    {/* Background glow */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold/5 rounded-full blur-3xl -z-10" data-id="element-15"></div>

    <div className="container mx-auto max-w-5xl" data-id="element-16">
      <div className="text-center mb-12 space-y-6" data-id="element-17">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight text-white drop-shadow-lg" data-id="element-18">
          Cómo Recuperar la Energía y Vitalidad de los 20 en solo{' '}
          <span className="text-gold" data-id="element-19">30 Días</span>
        </h1>
        <h2 className="text-2xl md:text-3xl text-gray-300 font-oswald font-normal" data-id="element-20">
          Sin Fármacos, Sin Dietas de Hambre y Sin Pasar Horas en el Gimnasio
        </h2>
        <p className="text-lg md:text-xl text-gray-light max-w-3xl mx-auto leading-relaxed mt-6 border-l-4 border-gold pl-6 text-left italic" data-id="element-21">
          — Incluso Si Tienes Más de 40 Años y Sientes Que Tu "Mejor Momento" Ya
          Pasó. Descubre el protocolo de reinicio biológico que los hombres de
          alto rendimiento utilizan para eliminar la barriga de estrés,
          recuperar la claridad mental y despertar cada mañana con una potencia
          que creían perdida.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-12 items-center mt-16" data-id="element-22">
        <EbookImage data-id="element-23" />
        <div className="space-y-8" data-id="element-24">
          <div className="space-y-4" data-id="element-25">
            <div className="flex items-start gap-3" data-id="element-26">
              <CheckCircle2 className="w-6 h-6 text-gold shrink-0 mt-1" data-id="element-27" />
              <p className="text-lg" data-id="element-28">
                Elimina la "barriga de estrés" resistente al ejercicio.
              </p>
            </div>
            <div className="flex items-start gap-3" data-id="element-29">
              <CheckCircle2 className="w-6 h-6 text-gold shrink-0 mt-1" data-id="element-30" />
              <p className="text-lg" data-id="element-31">
                Recupera el deseo y la potencia de tu juventud.
              </p>
            </div>
            <div className="flex items-start gap-3" data-id="element-32">
              <CheckCircle2 className="w-6 h-6 text-gold shrink-0 mt-1" data-id="element-33" />
              <p className="text-lg" data-id="element-34">
                Despierta con claridad mental y enfoque láser.
              </p>
            </div>
          </div>

          <div className="pt-6" data-id="element-35">
            <CTAButton data-id="element-36">SÍ, QUIERO EL PROTOCOLO AHORA</CTAButton>
            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-400" data-id="element-37">
              <ShieldCheck className="w-4 h-4" data-id="element-38" />
              <span data-id="element-39">Pago 100% Seguro y Privado</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
const HookAndPain = () => <section className="py-16 bg-charcoal-800 px-4 border-y border-charcoal-700" data-id="element-40">
    <div className="container mx-auto max-w-4xl space-y-16" data-id="element-41">
      {/* El Gancho */}
      <div className="space-y-6" data-id="element-42">
        <h2 className="text-3xl md:text-4xl text-center text-gold mb-8" data-id="element-43">
          ¿Te has mirado al espejo últimamente...
        </h2>
        <p className="text-lg md:text-xl leading-relaxed" data-id="element-44">
          ...y te has preguntado a dónde se fue ese hombre que podía comer lo
          que quisiera, trabajar 12 horas y aún tener energía para su pareja?
        </p>
        <p className="text-lg md:text-xl leading-relaxed font-medium text-white" data-id="element-45">
          No estás solo.
        </p>
        <p className="text-lg md:text-xl leading-relaxed" data-id="element-46">
          La mayoría de los hombres de 40+ aceptan el cansancio, la niebla
          mental y la falta de deseo como "parte de la edad". Pero la ciencia de
          2026 demuestra que no eres viejo...{' '}
          <strong className="text-gold" data-id="element-47">Haz tenido malos hábitos.</strong>
        </p>
      </div>

      {/* Agitación del Dolor */}
      <div className="bg-charcoal-900 p-8 md:p-10 rounded-lg border border-charcoal-700 shadow-xl relative" data-id="element-48">
        <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-charcoal-800 px-6 py-2 border border-charcoal-700 rounded-full" data-id="element-49">
          <span className="font-oswald text-red-400 tracking-wider" data-id="element-50">
            LA DURA REALIDAD
          </span>
        </div>
        <div className="space-y-6 mt-4" data-id="element-51">
          <p className="text-lg leading-relaxed" data-id="element-52">
            Es frustrante. Trabajas duro para proveer a tu familia, pero al
            llegar a casa lo único que quieres es el sofá. Te sientes irritable.
            Tu ropa ya no te queda igual porque esa "barriga de estrés" parece
            ser inmune a cualquier esfuerzo.
          </p>
          <p className="text-lg leading-relaxed" data-id="element-53">
            Peor aún es esa sensación de estar perdiendo tu "ventaja". En la
            oficina, los más jóvenes parecen ir más rápido. En casa, la conexión
            con tu pareja se siente como una obligación más que como una pasión.
          </p>
          <p className="text-xl leading-relaxed text-white font-medium border-l-4 border-red-500 pl-4" data-id="element-54">
            El miedo real no es el paso del tiempo, es convertirte en un
            espectador de tu propia vida, viendo cómo tu vitalidad se evapora
            mientras esperas que una solución mágica caiga del cielo.
          </p>
        </div>
      </div>
    </div>
  </section>;
const Revelation = () => <section className="py-20 px-4" data-id="element-55">
    <div className="container mx-auto max-w-4xl" data-id="element-56">
      <div className="text-center mb-12" data-id="element-57">
        <h2 className="text-3xl md:text-5xl mb-6" data-id="element-58">El Reinicio Biológico</h2>
        <p className="text-xl text-gold font-medium" data-id="element-59">
          Por qué todo lo que has intentado ha fallado
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-60">
        <div className="space-y-6" data-id="element-61">
          <p className="text-lg leading-relaxed" data-id="element-62">
            La razón por la que nada de lo que has intentado funciona (dietas
            keto, entrenamientos brutales, suplementos caros) es porque ignoran
            la causa raíz:{' '}
            <strong className="text-white" data-id="element-63">
              tu entorno está saboteando tus hormonas.
            </strong>
          </p>
          <p className="text-lg leading-relaxed" data-id="element-64">
            Vivimos en un mundo que le dice a tu cuerpo que debe estar en "modo
            supervivencia", elevando el cortisol y apagando tu caldera de
            testosterona.
          </p>
          <div className="bg-gold/10 border border-gold/30 p-6 rounded-lg mt-6" data-id="element-65">
            <h3 className="text-gold text-xl mb-3 flex items-center gap-2" data-id="element-66">
              <Zap className="w-5 h-5" data-id="element-67" /> La Solución Real
            </h3>
            <p className="text-white leading-relaxed" data-id="element-68">
              La solución no es forzar el sistema con fármacos que te hacen
              dependiente de por vida. La solución es un{' '}
              <strong data-id="element-69">Reinicio Biológico</strong>. Al dar las señales correctas
              de luz, carga y sueño, puedes "engañar" a tu cuerpo para que
              vuelva a producir niveles hormonales de juventud. Es como resetear
              el software de una computadora que se ha vuelto lenta.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-70">
          <div className="bg-charcoal-800 p-6 rounded-lg text-center border border-charcoal-700" data-id="element-71">
            <Brain className="w-10 h-10 text-gold mx-auto mb-3" data-id="element-72" />
            <h4 className="text-white font-oswald text-lg" data-id="element-73">Claridad</h4>
            <p className="text-sm text-gray-400 mt-2" data-id="element-74">
              Elimina la niebla mental
            </p>
          </div>
          <div className="bg-charcoal-800 p-6 rounded-lg text-center border border-charcoal-700" data-id="element-75">
            <Flame className="w-10 h-10 text-gold mx-auto mb-3" data-id="element-76" />
            <h4 className="text-white font-oswald text-lg" data-id="element-77">Metabolismo</h4>
            <p className="text-sm text-gray-400 mt-2" data-id="element-78">Quema grasa en reposo</p>
          </div>
          <div className="bg-charcoal-800 p-6 rounded-lg text-center border border-charcoal-700" data-id="element-79">
            <Clock className="w-10 h-10 text-gold mx-auto mb-3" data-id="element-80" />
            <h4 className="text-white font-oswald text-lg" data-id="element-81">Juventud</h4>
            <p className="text-sm text-gray-400 mt-2" data-id="element-82">
              Revierte el reloj biológico
            </p>
          </div>
          <div className="bg-charcoal-800 p-6 rounded-lg text-center border border-charcoal-700" data-id="element-83">
            <Dumbbell className="w-10 h-10 text-gold mx-auto mb-3" data-id="element-84" />
            <h4 className="text-white font-oswald text-lg" data-id="element-85">Potencia</h4>
            <p className="text-sm text-gray-400 mt-2" data-id="element-86">
              Fuerza y deseo renovados
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>;
const TestimonialCard = ({
  quote,
  author,
  result
}: {
  quote: string;
  author: string;
  result: string;
}) => <div className="bg-charcoal-800 p-8 rounded-lg border border-charcoal-700 relative" data-id="element-87">
    <div className="flex gap-1 mb-4" data-id="element-88">
      {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-gold text-gold" data-id="element-89" />)}
    </div>
    <p className="text-lg italic text-gray-300 mb-6 leading-relaxed" data-id="element-90">
      "{quote}"
    </p>
    <div className="border-t border-charcoal-600 pt-4" data-id="element-91">
      <p className="font-oswald text-white text-lg" data-id="element-92">{author}</p>
      <p className="text-gold font-medium mt-1" data-id="element-93">Resultado: {result}</p>
    </div>
  </div>;
const SocialProof = () => <section className="py-20 bg-charcoal-800 px-4 border-y border-charcoal-700" data-id="element-94">
    <div className="container mx-auto max-w-6xl" data-id="element-95">
      <div className="text-center mb-16" data-id="element-96">
        <h2 className="text-3xl md:text-5xl mb-4" data-id="element-97">
          Hombres Reales. Resultados Reales.
        </h2>
        <p className="text-xl text-gray-400" data-id="element-98">
          No tienes que creernos a nosotros, escúchalos a ellos.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8" data-id="element-99">
        <TestimonialCard quote="A los 47 años pensaba que mi vida sexual y mi energía estaban terminadas. Seguí el protocolo de reinicio de 30 días y los resultados fueron absurdos. Perdí 6 kilos de grasa abdominal y mi libido volvió a niveles que no sentía desde la universidad. Lo mejor es que solo entreno 20 minutos." author="Carlos M., 47 años" result="-6kg y energía duplicada" data-id="element-100" />
        <TestimonialCard quote="Como ejecutivo, la niebla mental me estaba matando. No podía concentrarme. Este sistema me enseñó a hackear mi sueño y mi alimentación. En dos semanas, la claridad mental volvió. Es como si alguien hubiera encendido la luz en mi cerebro de nuevo." author="Andrés R., 52 años" result="Claridad mental y mejor enfoque" data-id="element-101" />
        <TestimonialCard quote="Era muy escéptico. Pensaba que necesitaba TRT (inyecciones). Decidí darle una oportunidad a lo natural primero. Mis análisis de sangre mostraron un aumento del 35% en testo libre en un mes. Mi médico no lo podía creer." author="Miguel S., 43 años" result="+35% Testosterona libre" data-id="element-102" />
      </div>
    </div>
  </section>;
const Offer = () => <section className="py-20 px-4" data-id="element-103">
    <div className="container mx-auto max-w-5xl" data-id="element-104">
      <div className="text-center mb-16" data-id="element-105">
        <h2 className="text-4xl md:text-5xl text-gold mb-4" data-id="element-106">
          Lo que vas a recibir hoy
        </h2>
        <p className="text-xl text-white" data-id="element-107">
          El arsenal completo para tu transformación biológica
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-108">
        <div className="lg:col-span-5" data-id="element-109">
          <div className="sticky top-24" data-id="element-110">
            <EbookImage data-id="element-111" />
          </div>
        </div>

        <div className="lg:col-span-7 space-y-6" data-id="element-112">
          {/* Main Product */}
          <div className="bg-charcoal-800 p-6 rounded-lg border border-gold/50 shadow-[0_0_15px_rgba(212,168,67,0.15)]" data-id="element-113">
            <div className="flex justify-between items-start mb-2" data-id="element-114">
              <h3 className="text-2xl text-white" data-id="element-115">CÓDIGO TESTOSTERONA 40+</h3>
              <span className="text-gray-400 line-through text-lg" data-id="element-116">
                Valor $67
              </span>
            </div>
            <p className="text-gold font-medium mb-2" data-id="element-117">
              El mapa paso a paso para el reinicio hormonal.
            </p>
            <p className="text-gray-400 text-sm" data-id="element-118">
              El manual principal con la ciencia y la aplicación práctica para
              resetear tu sistema en 30 días.
            </p>
          </div>

          {/* Bonuses */}
          <div className="space-y-4" data-id="element-119">
            <h4 className="font-oswald text-xl text-white border-b border-charcoal-700 pb-2 mt-8 mb-4" data-id="element-120">
              BONOS DE ACCIÓN RÁPIDA INCLUIDOS:
            </h4>

            {[{
            title: 'Lista de Súper Hormonal',
            desc: 'Qué comprar y qué evitar en el supermercado.',
            val: '$27'
          }, {
            title: 'Protocolo Suplementos',
            desc: 'La verdad de lo que sí funciona y lo que es basura.',
            val: '$27'
          }, {
            title: 'Hack Óxido Nítrico',
            desc: 'Secretos para virilidad y flujo sanguíneo óptimo.',
            val: '$27'
          }, {
            title: 'Testo en la Oficina',
            desc: 'Estrategias para rendimiento ejecutivo élite.',
            val: '$17'
          }].map((bonus, idx) => <div key={idx} className="flex gap-4 items-start bg-charcoal-800/50 p-4 rounded border border-charcoal-700" data-id="element-121">
                <div className="bg-gold/20 p-2 rounded text-gold shrink-0" data-id="element-122">
                  <Zap className="w-5 h-5" data-id="element-123" />
                </div>
                <div className="flex-grow" data-id="element-124">
                  <div className="flex justify-between items-center" data-id="element-125">
                    <h5 className="text-white font-medium text-lg" data-id="element-126">
                      Bono #{idx + 1}: {bonus.title}
                    </h5>
                    <span className="text-gray-500 line-through text-sm" data-id="element-127">
                      Valor {bonus.val}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mt-1" data-id="element-128">{bonus.desc}</p>
                </div>
              </div>)}
          </div>

          {/* Value Stack Total */}
          <div id="precio" className="mt-8 p-6 bg-charcoal-900 border-2 border-dashed border-charcoal-600 rounded-lg text-center" data-id="element-129">
            <p className="text-xl text-gray-300 mb-2" data-id="element-130">
              Valor Total Real:{' '}
              <span className="line-through text-red-400" data-id="element-131">$165 USD</span>
            </p>
            <p className="text-3xl md:text-4xl text-white font-bold mb-6" data-id="element-132">
              Hoy Solo:{' '}
              <span className="text-gold text-5xl md:text-6xl" data-id="element-133">$16.5</span>{' '}
              <span className="text-2xl" data-id="element-134">USD</span>
            </p>
            <CTAButton className="w-full text-lg md:text-xl py-5" href="https://www.legadosalud.com/cart/42440283619413:1" data-id="element-135">
              ACCESO INMEDIATO POR $16.5 USD
            </CTAButton>
            <p className="text-sm text-gray-500 mt-4" data-id="element-136">
              Acceso digital instantáneo. Pago único.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>;
const UrgencyAndFAQ = () => <section className="py-20 bg-charcoal-800 px-4 border-t border-charcoal-700" data-id="element-137">
    <div className="container mx-auto max-w-4xl" data-id="element-138">
      {/* Urgency Block */}
      <div className="bg-charcoal-900 p-8 rounded-lg border border-red-900/50 text-center mb-20" data-id="element-139">
        <h3 className="text-2xl md:text-3xl text-white mb-4" data-id="element-140">
          ¿Por qué tan barato?
        </h3>
        <p className="text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto" data-id="element-141">
          Si fueras a una clínica de longevidad, pagarías más de $2,000 por este
          conocimiento. Incluso un entrenador personal decente te costaría $300
          al mes. Hoy, no pagarás $165... ni siquiera el precio regular de $67.
        </p>
        <p className="text-xl text-gold font-medium mt-6" data-id="element-142">
          Estamos en fase de lanzamiento y queremos recoger 500 testimonios
          antes de subir el precio a su valor real.
        </p>
      </div>

      {/* FAQ */}
      <div className="space-y-8" data-id="element-143">
        <h2 className="text-3xl md:text-4xl text-center mb-10" data-id="element-144">
          Preguntas Frecuentes
        </h2>

        <div className="space-y-6" data-id="element-145">
          <div className="bg-charcoal-900 p-6 rounded-lg border border-charcoal-700" data-id="element-146">
            <h4 className="text-xl text-white mb-3 flex gap-3 items-start" data-id="element-147">
              <span className="text-gold font-bold" data-id="element-148">❓</span>
              "¿Esto requiere que deje de comer lo que me gusta?"
            </h4>
            <p className="text-gray-300 ml-8 leading-relaxed" data-id="element-149">
              <span className="text-green-500 font-bold mr-2" data-id="element-150">✅</span>
              No. Aprenderás a usar carbohidratos estratégicamente para bajar el
              cortisol por la noche. No es una dieta de privación, es una de
              optimización.
            </p>
          </div>

          <div className="bg-charcoal-900 p-6 rounded-lg border border-charcoal-700" data-id="element-151">
            <h4 className="text-xl text-white mb-3 flex gap-3 items-start" data-id="element-152">
              <span className="text-gold font-bold" data-id="element-153">❓</span>
              "¿Tengo que pasar horas en el gimnasio?"
            </h4>
            <p className="text-gray-300 ml-8 leading-relaxed" data-id="element-154">
              <span className="text-green-500 font-bold mr-2" data-id="element-155">✅</span>
              Todo lo contrario. El exceso de ejercicio eleva el cortisol.
              Nuestro protocolo "Hierro Profundo" solo requiere 20 minutos, 3
              veces por semana.
            </p>
          </div>

          <div className="bg-charcoal-900 p-6 rounded-lg border border-charcoal-700" data-id="element-156">
            <h4 className="text-xl text-white mb-3 flex gap-3 items-start" data-id="element-157">
              <span className="text-gold font-bold" data-id="element-158">❓</span>
              "¿Funciona si tengo más de 55 años?"
            </h4>
            <p className="text-gray-300 ml-8 leading-relaxed" data-id="element-159">
              <span className="text-green-500 font-bold mr-2" data-id="element-160">✅</span>
              Absolutamente. La biología humana responde a los estímulos sin
              importar la edad. Nunca es tarde para resetear tu software
              hormonal.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>;
const FooterClose = () => <section className="py-24 px-4 relative overflow-hidden" data-id="element-161">
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-64 bg-gold/5 blur-3xl -z-10 rounded-t-full" data-id="element-162"></div>

    <div className="container mx-auto max-w-4xl text-center" data-id="element-163">
      <h2 className="text-4xl md:text-5xl text-white mb-12" data-id="element-164">
        Tienes dos caminos frente a ti.
      </h2>

      <div className="grid md:grid-cols-2 gap-8 mb-16 text-left" data-id="element-165">
        <div className="bg-charcoal-800/50 p-8 rounded-lg border border-charcoal-700 opacity-70" data-id="element-166">
          <h3 className="text-2xl text-gray-400 mb-4 border-b border-charcoal-600 pb-2" data-id="element-167">
            Camino A: No hacer nada
          </h3>
          <p className="text-gray-400 leading-relaxed" data-id="element-168">
            Seguir sintiéndote cansado, viendo cómo tu cuerpo se deteriora y
            aceptando la mediocridad biológica como tu "nueva realidad".
          </p>
        </div>

        <div className="bg-charcoal-800 p-8 rounded-lg border border-gold shadow-[0_0_30px_rgba(212,168,67,0.1)] transform md:-translate-y-4" data-id="element-169">
          <h3 className="text-2xl text-gold mb-4 border-b border-gold/30 pb-2" data-id="element-170">
            Camino B: Tomar el control hoy
          </h3>
          <p className="text-white leading-relaxed font-medium" data-id="element-171">
            Aplicar un sistema probado y ver cómo en 30 días recuperas la
            chispa, el músculo y la autoridad que te corresponden.
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto" data-id="element-172">
        <CTAButton className="w-full py-6 text-xl md:text-2xl shadow-2xl shadow-gold/20" data-id="element-173">
          QUIERO RECUPERAR MI VITALIDAD
        </CTAButton>
        <p className="mt-6 text-gray-400 font-medium" data-id="element-174">
          Acceso Inmediato por solo $16.5 USD
        </p>
      </div>
    </div>
  </section>;
const LegalModal = ({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) => {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" onClick={onClose} data-id="element-175">
      <div className="bg-charcoal-800 border border-charcoal-700 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-176">
        <div className="sticky top-0 bg-charcoal-800 border-b border-charcoal-700 px-6 py-4 flex items-center justify-between" data-id="element-177">
          <h3 className="font-oswald text-xl text-gold uppercase tracking-wider" data-id="element-178">
            {title}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors text-2xl leading-none" aria-label="Cerrar" data-id="element-179">
            ✕
          </button>
        </div>
        <div className="px-6 py-6 text-gray-300 text-sm leading-relaxed space-y-4" data-id="element-180">
          {children}
        </div>
      </div>
    </div>;
};
const Footer = () => {
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  return <>
      <footer className="bg-[#0a0a0a] py-8 border-t border-charcoal-800 text-center text-sm text-gray-600" data-id="element-181">
        <div className="container mx-auto px-4" data-id="element-182">
          <p data-id="element-183">© 2026 Código Testosterona. Todos los derechos reservados.</p>
          <p className="mt-2 text-xs max-w-2xl mx-auto" data-id="element-184">
            Este producto no sustituye el consejo médico profesional. Los
            resultados pueden variar. Consulta a tu médico antes de iniciar
            cualquier programa de ejercicios o nutrición.
          </p>
          <div className="mt-4 flex items-center justify-center gap-6" data-id="element-185">
            <button onClick={() => setShowTerms(true)} className="text-gray-500 hover:text-gold transition-colors underline underline-offset-2 text-xs" data-id="element-186">
              Términos y Condiciones
            </button>
            <span className="text-charcoal-700" data-id="element-187">|</span>
            <button onClick={() => setShowPrivacy(true)} className="text-gray-500 hover:text-gold transition-colors underline underline-offset-2 text-xs" data-id="element-188">
              Política de Privacidad
            </button>
          </div>
        </div>
      </footer>

      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Términos y Condiciones" data-id="element-189">
        <p data-id="element-190">
          <strong className="text-white" data-id="element-191">1. Aceptación de los Términos</strong>
        </p>
        <p data-id="element-192">
          Al acceder y adquirir el producto digital "Código Testosterona 40+",
          aceptas estos términos y condiciones en su totalidad. Si no estás de
          acuerdo con alguna parte, te recomendamos no realizar la compra.
        </p>

        <p data-id="element-193">
          <strong className="text-white" data-id="element-194">2. Naturaleza del Producto</strong>
        </p>
        <p data-id="element-195">
          Este es un producto digital de carácter informativo y educativo. No
          constituye asesoramiento médico, diagnóstico ni tratamiento. Los
          contenidos están basados en investigación disponible públicamente y
          experiencias personales.
        </p>

        <p data-id="element-196">
          <strong className="text-white" data-id="element-197">3. Resultados</strong>
        </p>
        <p data-id="element-198">
          Los testimonios y resultados mencionados son experiencias individuales
          y no garantizan que obtendrás los mismos resultados. Los resultados
          pueden variar según factores individuales como edad, estado de salud,
          adherencia al protocolo y otros factores personales.
        </p>

        <p data-id="element-199">
          <strong className="text-white" data-id="element-200">4. Responsabilidad del Usuario</strong>
        </p>
        <p data-id="element-201">
          Es tu responsabilidad consultar con un profesional de la salud antes
          de implementar cualquier cambio en tu dieta, rutina de ejercicios o
          estilo de vida. El uso de la información proporcionada es bajo tu
          propia responsabilidad.
        </p>

        <p data-id="element-202">
          <strong className="text-white" data-id="element-203">5. Propiedad Intelectual</strong>
        </p>
        <p data-id="element-204">
          Todo el contenido del producto está protegido por derechos de autor.
          Queda prohibida su reproducción, distribución o reventa sin
          autorización expresa por escrito.
        </p>

        <p data-id="element-205">
          <strong className="text-white" data-id="element-206">6. Política de Reembolso</strong>
        </p>
        <p data-id="element-207">
          Debido a la naturaleza digital del producto y su entrega inmediata,
          todas las ventas son finales. No se ofrecen reembolsos una vez que se
          ha accedido al contenido.
        </p>

        <p data-id="element-208">
          <strong className="text-white" data-id="element-209">7. Modificaciones</strong>
        </p>
        <p data-id="element-210">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Las modificaciones entrarán en vigor desde su publicación.
        </p>
      </LegalModal>

      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Política de Privacidad" data-id="element-211">
        <p data-id="element-212">
          <strong className="text-white" data-id="element-213">1. Información que Recopilamos</strong>
        </p>
        <p data-id="element-214">
          Al realizar tu compra, recopilamos únicamente la información necesaria
          para procesar la transacción: nombre, dirección de correo electrónico
          y datos de pago (procesados de forma segura por nuestro proveedor de
          pagos).
        </p>

        <p data-id="element-215">
          <strong className="text-white" data-id="element-216">2. Uso de la Información</strong>
        </p>
        <p data-id="element-217">
          Tu información personal se utiliza exclusivamente para: entregar el
          producto digital adquirido, enviar actualizaciones relacionadas con tu
          compra y mejorar nuestros servicios.
        </p>

        <p data-id="element-218">
          <strong className="text-white" data-id="element-219">3. Protección de Datos</strong>
        </p>
        <p data-id="element-220">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado,
          alteración, divulgación o destrucción.
        </p>

        <p data-id="element-221">
          <strong className="text-white" data-id="element-222">
            4. No Compartimos tu Información
          </strong>
        </p>
        <p data-id="element-223">
          No vendemos, alquilamos ni compartimos tu información personal con
          terceros, excepto cuando sea necesario para procesar tu pago o cuando
          la ley lo requiera.
        </p>

        <p data-id="element-224">
          <strong className="text-white" data-id="element-225">5. Cookies</strong>
        </p>
        <p data-id="element-226">
          Nuestro sitio puede utilizar cookies para mejorar la experiencia del
          usuario y con fines analíticos. Puedes configurar tu navegador para
          rechazar cookies, aunque esto podría afectar la funcionalidad del
          sitio.
        </p>

        <p data-id="element-227">
          <strong className="text-white" data-id="element-228">6. Tus Derechos</strong>
        </p>
        <p data-id="element-229">
          Tienes derecho a acceder, rectificar o solicitar la eliminación de tus
          datos personales en cualquier momento. Para ejercer estos derechos,
          puedes comunicarte con nosotros.
        </p>

        <p data-id="element-230">
          <strong className="text-white" data-id="element-231">7. Cambios en esta Política</strong>
        </p>
        <p data-id="element-232">
          Nos reservamos el derecho de actualizar esta política de privacidad.
          Cualquier cambio será publicado en esta página con la fecha de última
          actualización.
        </p>

        <p className="text-gray-500 italic mt-4" data-id="element-233">
          Última actualización: Abril 2026
        </p>
      </LegalModal>
    </>;
};
export function App() {
  return <div className="min-h-screen bg-charcoal-900 font-inter text-gray-light selection:bg-gold selection:text-charcoal-900" data-id="element-234">
      <StickyBar data-id="element-235" />
      <main data-id="element-236">
        <Hero data-id="element-237" />
        <SectionDivider data-id="element-238" />
        <HookAndPain data-id="element-239" />
        <Revelation data-id="element-240" />
        <SectionDivider data-id="element-241" />
        <SocialProof data-id="element-242" />
        <Offer data-id="element-243" />
        <UrgencyAndFAQ data-id="element-244" />
        <FooterClose data-id="element-245" />
      </main>
      <Footer data-id="element-246" />
    </div>;
}